import { useCallback, useEffect, useState } from "react";
import { FiLoader, FiRefreshCw, FiSave } from "react-icons/fi";
import { getAdminFees, updateAdminFees } from "../api/admin";
import { adminTheme } from "../styles/adminTheme";

/**
 * What Mintly keeps, and what Keepz costs, per payment method.
 *
 * Both sides matter: Keepz's rate decides what actually reaches us, so a rate they
 * renegotiate and nobody records here would over-credit every seller.
 *
 * A blank field inherits — the rail falls back to the overall rate, and that falls back
 * to the server's environment. The inherited figure is shown beneath the input, so a
 * blank field never looks like a zero.
 *
 * @see mintly-back/src/services/feeSettings
 */

const SAMPLE = 100;

const pct = (n) => `${Number(n || 0)}%`;
const money = (n) => `₾${Number(n || 0).toFixed(2)}`;

/** Empty means "inherit", so it must survive the round trip as null, not as 0. */
const toRate = (value) => {
  const trimmed = String(value ?? "").trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : null;
};

const asField = (value) => (value === null || value === undefined ? "" : String(value));

/** A money amount, where empty means "inherit" rather than zero. */
const toAmount = (value) => {
  const trimmed = String(value ?? "").trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? Math.max(0, Math.round(n * 100) / 100) : null;
};

const AdminFeesTab = ({ isDark, setToast, baseText, mutedText, cardBase }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const theme = adminTheme(isDark);
  const { inputBase, rowBorder } = theme;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminFees();
      setData(res.data);
      setForm({
        saleFeePercent: asField(res.data.saleFeePercent),
        creatorFeePercent: asField(res.data.creatorFeePercent),
        referralPercent: asField(res.data.referralPercent),
        withdrawal: {
          mintlyFlat: asField(res.data.withdrawal?.mintlyFlat),
          keepzFlat: asField(res.data.withdrawal?.keepzFlat),
          minAmount: asField(res.data.withdrawal?.minAmount),
          maxAmount: asField(res.data.withdrawal?.maxAmount),
        },
        methods: Object.fromEntries(
          res.data.methods.map((m) => [
            m.group,
            { mintlyPercent: asField(m.mintlyPercent), keepzPercent: asField(m.keepzPercent) },
          ])
        ),
      });
    } catch (err) {
      setToast?.(err?.response?.data?.message || "Could not load fees");
    } finally {
      setLoading(false);
    }
  }, [setToast]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const res = await updateAdminFees({
        saleFeePercent: toRate(form.saleFeePercent),
        creatorFeePercent: toRate(form.creatorFeePercent),
        referralPercent: toRate(form.referralPercent),
        withdrawal: Object.fromEntries(
          Object.entries(form.withdrawal).map(([k, v]) => [k, toAmount(v)])
        ),
        methods: Object.fromEntries(
          Object.entries(form.methods).map(([group, rate]) => [
            group,
            {
              mintlyPercent: toRate(rate.mintlyPercent),
              keepzPercent: toRate(rate.keepzPercent),
            },
          ])
        ),
      });
      setData(res.data);
      setToast?.("Rates saved — they apply to the next payment");
    } catch (err) {
      setToast?.(err?.response?.data?.message || "Could not save fees");
    } finally {
      setSaving(false);
    }
  };

  const setMethod = (group, field, value) =>
    setForm((prev) => ({
      ...prev,
      methods: { ...prev.methods, [group]: { ...prev.methods[group], [field]: value } },
    }));

  if (loading || !form) {
    return (
      <div className={`${cardBase} rounded-2xl p-10 flex items-center justify-center gap-2 ${mutedText}`}>
        <FiLoader className="h-5 w-5 animate-spin" /> Loading rates…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className={`${cardBase} rounded-2xl p-5 sm:p-6`}>
        <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
          <div>
            <h3 className={`text-base font-semibold tracking-tight ${baseText}`}>Rates</h3>
            <p className={`text-xs ${mutedText} mt-1 max-w-xl`}>
              Leave a field empty to inherit. A mint space with its own agreed rate still
              overrides everything here.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            className={`${theme.btnSecondary}`}
          >
            <FiRefreshCw className="h-4 w-4" />
            Reload
          </button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 mb-6">
          {[
            {
              key: "saleFeePercent",
              label: "Mintly's cut of a product sale",
              effective: data?.effective?.saleFeePercent,
            },
            {
              key: "creatorFeePercent",
              label: "Mintly's cut of Creator Rewards",
              effective: data?.effective?.creatorFeePercent,
            },
          ].map((row) => (
            <div key={row.key} className={`rounded-xl p-4 ${theme.inset}`}>
              <label className={`block text-sm font-semibold ${baseText}`} htmlFor={row.key}>
                {row.label}
              </label>
              <div className="mt-2 flex items-center gap-2">
                <input
                  id={row.key}
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={form[row.key]}
                  onChange={(e) => setForm((prev) => ({ ...prev, [row.key]: e.target.value }))}
                  placeholder="inherit"
                  className={`w-28 rounded-xl border px-3 py-2 text-sm outline-none ${inputBase}`}
                />
                <span className={`text-sm ${mutedText}`}>%</span>
              </div>
              <p className={`mt-2 text-[11px] ${mutedText}`}>
                In force now: {pct(row.effective)}
              </p>
            </div>
          ))}
        </div>

        <div className={theme.tableWrap}>
          <table className="w-full text-sm">
            <thead>
              <tr className={`text-left ${theme.thead}`}>
                {["Payment method", "Mintly keeps", "Keepz charges", `On a ${money(SAMPLE)} sale`].map(
                  (h) => (
                    <th key={h} className={theme.th}>
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {data.methods.map((m) => {
                const mintly = toRate(form.methods[m.group]?.mintlyPercent);
                const keepz = toRate(form.methods[m.group]?.keepzPercent);
                // Blank inherits, so the example uses what the server says is in force.
                const mintlyPct = mintly ?? m.effectiveMintlyPercent;
                const keepzPct = keepz ?? m.effectiveKeepzPercent;
                const keepzCut = (SAMPLE * keepzPct) / 100;
                const mintlyCut = (SAMPLE * mintlyPct) / 100;
                return (
                  <tr key={m.group} className={`border-t ${rowBorder} ${theme.rowHover}`}>
                    <td className={`px-4 py-3 font-semibold ${baseText}`}>{m.label}</td>
                    {[
                      ["mintlyPercent", m.effectiveMintlyPercent],
                      ["keepzPercent", m.effectiveKeepzPercent],
                    ].map(([field, effective]) => (
                      <td key={field} className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={form.methods[m.group]?.[field] ?? ""}
                            onChange={(e) => setMethod(m.group, field, e.target.value)}
                            placeholder="inherit"
                            aria-label={`${m.label} ${field}`}
                            className={`w-24 rounded-xl border px-3 py-1.5 text-sm outline-none ${inputBase}`}
                          />
                          <span className={`text-[11px] ${mutedText}`}>{pct(effective)} now</span>
                        </div>
                      </td>
                    ))}
                    <td className={`px-4 py-3 ${mutedText}`}>
                      Keepz {money(keepzCut)} · Mintly {money(mintlyCut)} ·{" "}
                      <span className={baseText}>
                        seller {money(SAMPLE - keepzCut - mintlyCut)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className={`mt-6 rounded-xl p-4 ${theme.inset}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h4 className={`text-sm font-bold ${baseText}`}>Withdrawals</h4>
            <p className={`text-[11px] ${mutedText}`}>
              Our fee comes out of what the user asked for — they see what will land in
              their account before confirming.
            </p>
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ["mintlyFlat", "Mintly charges", "₾ per withdrawal", data.withdrawal?.effective?.mintlyFlat],
              ["keepzFlat", "Keepz charges us", "₾ per withdrawal", data.withdrawal?.effective?.keepzFlat],
              ["minAmount", "Smallest withdrawal", "₾ at once", data.withdrawal?.effective?.min],
              ["maxAmount", "Largest withdrawal", "₾ at once", data.withdrawal?.effective?.max],
            ].map(([key, label, unit, effective]) => (
              <div key={key}>
                <label className={`block text-[11px] font-semibold ${baseText}`} htmlFor={`wd-${key}`}>
                  {label}
                </label>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    id={`wd-${key}`}
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.withdrawal?.[key] ?? ""}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        withdrawal: { ...prev.withdrawal, [key]: e.target.value },
                      }))
                    }
                    placeholder="inherit"
                    className={`w-24 rounded-xl border px-3 py-1.5 text-sm outline-none ${inputBase}`}
                  />
                  <span className={`text-[11px] ${mutedText}`}>{unit}</span>
                </div>
                <p className={`mt-1 text-[11px] ${mutedText}`}>{effective ?? 0} now</p>
              </div>
            ))}
          </div>

          {(() => {
            // The same arithmetic the server does, on a round number.
            const w = form.withdrawal || {};
            const eff = data.withdrawal?.effective || {};
            const charged = Math.min(SAMPLE, toAmount(w.mintlyFlat) ?? eff.mintlyFlat ?? 0);
            const net = SAMPLE - charged;
            const keepzCost = toAmount(w.keepzFlat) ?? eff.keepzFlat ?? 0;
            return (
              <p className={`mt-3 text-[11px] ${mutedText}`}>
                On a {money(SAMPLE)} withdrawal: we charge {money(charged)}, the user
                receives <span className={baseText}>{money(net)}</span>, Keepz costs us{" "}
                {money(keepzCost)} — leaving Mintly{" "}
                <span className={baseText}>{money(charged - keepzCost)}</span>.
              </p>
            );
          })()}
        </div>

        <div className={`mt-6 rounded-xl p-4 ${theme.inset}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h4 className={`text-sm font-bold ${baseText}`}>Invites</h4>
            <p className={`text-[11px] ${mutedText} max-w-xl`}>
              Paid out of Mintly's own fee, on everything the invited person earns — sales,
              subscription payments and Creator Rewards. Sellers and creators get the same
              either way. Empty or 0 pays nothing; people who join still count as invited.
            </p>
          </div>

          <div className="mt-3 flex flex-wrap items-end gap-x-6 gap-y-3">
            <div>
              <label className={`block text-[11px] font-semibold ${baseText}`} htmlFor="referralPercent">
                Inviter's share of Mintly's fee
              </label>
              <div className="mt-1 flex items-center gap-2">
                <input
                  id="referralPercent"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={form.referralPercent}
                  onChange={(e) => setForm((prev) => ({ ...prev, referralPercent: e.target.value }))}
                  placeholder="inherit"
                  className={`w-24 rounded-xl border px-3 py-1.5 text-sm outline-none ${inputBase}`}
                />
                <span className={`text-[11px] ${mutedText}`}>%</span>
              </div>
              <p className={`mt-1 text-[11px] ${mutedText}`}>
                In force now: {pct(data?.effective?.referralPercent)}
              </p>
            </div>

            {(() => {
              // The same arithmetic the server does, on a round number.
              const salePct = toRate(form.saleFeePercent) ?? data?.effective?.saleFeePercent ?? 0;
              const share = toRate(form.referralPercent) ?? data?.effective?.referralPercent ?? 0;
              const fee = (SAMPLE * salePct) / 100;
              const toInviter = Math.min(fee, (fee * share) / 100);
              return (
                <p className={`text-[11px] ${mutedText} pb-1`}>
                  On a {money(SAMPLE)} sale at {pct(salePct)}: Mintly's fee is {money(fee)}, the
                  inviter gets <span className={baseText}>{money(toInviter)}</span> of it, Mintly
                  keeps <span className={baseText}>{money(fee - toInviter)}</span>.
                </p>
              );
            })()}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-4">
          <p className={`text-[11px] ${mutedText}`}>
            Keepz's own rates are here because they decide what reaches us — if they
            change a rate and this isn't updated, every sale over-credits the seller.
          </p>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className={`${theme.btnPrimary} shrink-0`}
          >
            {saving ? <FiLoader className="h-4 w-4 animate-spin" /> : <FiSave className="h-4 w-4" />}
            Save rates
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminFeesTab;
